import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';
import { Box, CircularProgress } from '@material-ui/core';
import { formatValue } from 'react-currency-input-field';
import TopHeader from '../components/TopHeader';
import i18n from '../i18n/i18n';
import { getCustomerTransactionsHistory, getCustomerWallets } from '../api/api';
import { useUser } from '../contexts/UserContext';
import PrivateCardSlider from '../components/private-card-slider';
import PrivateCard from '../components/private-card';
import ActionButtons from '../components/ActionButtons';
import GalpUserMenu from '../components/GalpUserMenu';
import { getDate, getTime } from '../utils/DateFormatter';
import getImageFromGateway from '../utils/GatewayImages';
import Accordion from '../components/accordion';
import AccordionItem from '../components/accordion-item';
import { useTheme } from '../contexts/ThemeContext';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
`;

const StyledSpan = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 30px;
`;

const ListFiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1%;
  width: 100%;
`;

const FilteredList = styled.div`
  display: flex;
  padding-top: 4px;
  align-content: start;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
  overflow-y: auto;
  position: relative;
  flex-direction: row;
  align-items: center;
  margin: auto;
  height: 50dvh;
`;

const ListContainer = styled.div`
  display: flex;
  align-content: center;
  flex-wrap: wrap;
  width: 100%;
  flex: 1;
  overflow: auto;
  flex-direction: column;

`;

const FilterButton = styled.div<{
  selected?: boolean,
  bgColor?: string,
  inactiveBgColor?: string,
  inactiveTextColor?: string,
  borderRadius?: string,
}>`
  flex: 1;
  padding: 4%;
  background: ${(props) => (props.selected ? props.bgColor || 'rgb(141,197,62)' : props.inactiveBgColor || 'rgb(241,241,241)')};
  text-align: center;
  margin-top: 2%;
  margin-bottom: 2%;
  border-radius: ${(props) => props.borderRadius || '6px'};
  color: ${(props) => (props.selected ? 'rgb(255,255,255)' : props.inactiveTextColor || 'rgb(161,161,161)')};
  font-size: 1em;
`;

const Spacer = styled.div`
  min-width: 10px;
`;

const Label = styled.span`
  font-size: 0.85em;
  min-width: 110px;
  display: inline-block;
`;

const BoldLabel = styled.span`
  font-size: 0.85em;
  font-weight: bold;
  min-width: 110px;
  display: inline-block;
  width: 40%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const NoVMView = ({ history }) => {
  const [dataSource, setDataSource] = useState<any>([]);
  const [loading] = useState<boolean>(false);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const { user } = useUser();
  const [paymentMethod, setPaymentMethod] = useState<any>(undefined);
  const [customerWallets, setCustomerWallets] = useState([]);
  const [tab, setTab] = React.useState('ALL');
  const [error, setError] = useState<boolean>(false);
  const backRef = useRef<HTMLDivElement>(null);
  const { theme, themeName } = useTheme();
  const isGalp = themeName === 'galp';

  const galpInitials = (() => {
    const authUser = (user as any)?.user;
    const source = authUser?.name || (authUser?.email || '').split('@')[0];
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    const raw = parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : (parts[0]?.[0] || '');
    return raw.toUpperCase();
  })();

  const currentLanguage = useMemo(
    () => `${i18n.language[0]}${i18n.language[1]}-${i18n.language[2]}${i18n.language[3]}`,
    [i18n.language],
  );

  const handleListChange = (newValue: string) => {
    setTab(newValue);
  };

  useEffect(() => {
    setLoadingHistory(true);

    (async () => {
      try {
        if (paymentMethod?.wallet) {
          const transactions = await getCustomerTransactionsHistory(undefined, paymentMethod?.wallet?.id, tab);
          setDataSource(transactions);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [paymentMethod, tab]);

  useEffect(() => {
    (async () => {
      try {
        const finalPaymentMethods: any = [];
        const wallets = await getCustomerWallets(user);

        wallets.forEach((tokenWallet) => {
          finalPaymentMethods.push({
            gatewayName: 'TOKEN',
            wallet: tokenWallet,
          });
        });

        setPaymentMethod(finalPaymentMethods?.[0]);
        setCustomerWallets(finalPaymentMethods);
      } catch (err) {
        setShowNotification(true);
      }
    })();
  }, []);

  let message;

  switch (tab) {
    case 'ALL':
      message = 'no_purchases_registered';
      break;
    case 'PURCHASES':
      message = 'no_purchases_registered';
      break;
    case 'REVALUES':
      message = 'no_revalues_registered';
      break;
    default:
      message = '';
      break;
  }

  // @ts-ignore
  const backgroundHeight = backRef !== null ? (backRef?.current?.clientHeight + 40) || '57%' : '57%';

  const transactionsAccordion = (
    <Accordion key={34593384}>
      {dataSource.map((p: any) => {
        const revalueAmount = p?.currency === 'XXX'
          ? p.amount
          : (p.amount / 100)
            .toFixed(2);

        const notExpandable = (!p.productName && !p.vendingMachineName && !p.merchantName);

        return (
          <AccordionItem
            notExpandable={notExpandable}
            chevronMarginRight="10px"
            highlightColor="rgba(99,100,103,255)"
            header={(
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: notExpandable ? 'calc(100% - 34px)' : '100% !important',
                  maxWidth: notExpandable ? 'calc(100% - 34px)' : '100% !important',
                  flex: 1,
                  height: '100%',
                }}
              >
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '40%',
                  justifyContent: 'start',
                  alignItems: 'start',
                  height: '100%',
                  fontSize: '0.85em',
                }}
                >
                  <StyledSpan>{getDate(p.date)}</StyledSpan>
                  <StyledSpan>{getTime(p.date)}</StyledSpan>
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '35%',
                  justifyContent: 'center',
                  alignItems: 'start',
                  height: '100%',
                  fontSize: '0.85em',
                }}
                >
                  <StyledSpan>
                    {p?.currency === 'XXX'
                      ? `${revalueAmount} ${i18n.t('services')}`
                      : formatValue({
                        value: revalueAmount,
                        groupSeparator: ',',
                        decimalSeparator: '.',
                        decimalScale: 2,
                        intlConfig: { locale: currentLanguage, currency: p?.currency || 'EUR' },
                      })}
                  </StyledSpan>
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '25%',
                  justifyContent: 'center',
                  alignItems: 'start',
                  height: '100%',
                  fontSize: '0.85em',
                }}
                >
                  {getImageFromGateway(p.gateway?.name)}
                </div>
              </div>
            )}
          >
            <div style={{ width: 'calc(100% - 24px)' }}>
              <Row>
                <BoldLabel>{i18n.t('product')}</BoldLabel>
                <Label style={{
                  display: 'inline-block',
                  maxWidth: '60%',
                  verticalAlign: 'top',
                }}
                >
                  {p.productName}
                </Label>
              </Row>
              <Row>
                <BoldLabel>{i18n.t('machine')}</BoldLabel>
                <Label>{p.vendingMachineName}</Label>
              </Row>
              <Row>
                <BoldLabel>{i18n.t('merchant')}</BoldLabel>
                <Label>{p.merchantName}</Label>
              </Row>
            </div>
          </AccordionItem>
        );
      })}
    </Accordion>
  );

  if (isGalp) {
    const galpTabs = [
      { value: 'ALL', label: i18n.t('all') },
      { value: 'PURCHASES', label: i18n.t('movements') },
      { value: 'REVALUES', label: i18n.t('loads') },
    ];

    return (
      <div style={{
        background: '#d7cec5',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
        fontFamily: theme.bodyFont,
      }}
      >
        {/* Header */}
        <div style={{
          position: 'relative',
          background: '#f7f4ed',
          minHeight: '94px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 16px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <GalpUserMenu initials={galpInitials} />
        </div>

        {/* Card + actions */}
        <div style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px',
          padding: '18px 16px 10px',
        }}
        >
          <div style={{ width: '76%' }}>
            {paymentMethod && (
              <PrivateCard
                paymentMethod={paymentMethod}
                walletAmount={0}
                color="#be2e00"
                onClick={() => undefined}
              />
            )}
          </div>
          <ActionButtons history={history} paymentMethod={paymentMethod} menu="initialMenu" />
        </div>

        {/* Bottom sheet */}
        <div style={{
          flex: 1,
          background: 'white',
          borderTop: '2px solid #bababa',
          borderTopLeftRadius: '14px',
          borderTopRightRadius: '14px',
          marginTop: '8px',
          padding: '20px 12px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          boxSizing: 'border-box',
        }}
        >
          <div style={{ display: 'flex', width: '100%', flexShrink: 0 }}>
            {galpTabs.map((t) => {
              const active = tab === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => handleListChange(t.value)}
                  style={{
                    flex: 1,
                    height: '36px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: active ? '1px solid #be2e00' : '1px solid #eee',
                    color: active ? '#111110' : '#616061',
                    fontFamily: theme.bodyFont,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', marginTop: '16px' }}>
            {loadingHistory && (
              <Box display="flex" justifyContent="center" alignItems="center" marginTop="20%">
                <CircularProgress style={{ color: theme.loadingIconColor }} />
              </Box>
            )}
            {!loadingHistory && error && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                marginTop="20%"
              >
                <p style={{ textAlign: 'center', color: '#636467', margin: 0 }}>{i18n.t('error_ocurred')}</p>
                <p style={{ textAlign: 'center', color: '#636467', margin: 0 }}>{i18n.t('try_again_later')}</p>
              </Box>
            )}
            {!loadingHistory && !error && dataSource.length === 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                marginTop: '12%',
              }}
              >
                <img src="/companies/GALP/warning_icon.svg" alt="" style={{ width: '42px', height: '42px' }} />
                <p style={{
                  fontFamily: theme.bodyFont,
                  fontSize: '13px',
                  color: '#111110',
                  textAlign: 'center',
                  margin: 0,
                }}
                >
                  {i18n.t(message)}
                </p>
              </div>
            )}
            {!loadingHistory && !error && dataSource.length > 0 && transactionsAccordion}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Root>
      <div style={{
        background: theme.noVmViewBGColor,
        position: 'absolute',
        width: '100%',
        height: backgroundHeight,
        zIndex: 0,
      }}
      />
      <div
        ref={backRef}
        style={{
          flex: 1,
        }}
      >
        <TopHeader showVm={false} userLogo color={theme.iconColor} style={{ paddingLeft: '1em' }} />
        <PrivateCardSlider
          paymentMethods={customerWallets}
          loading={loading}
          showNotification={showNotification}
          onWalletChange={(selectedWallet) => setPaymentMethod(selectedWallet)}
          isInVendingMachine={false}
          color="rgb(250,194,3)"
        />
        <ActionButtons
          history={history}
          paymentMethod={paymentMethod}
          menu="initialMenu"
        />
      </div>
      <div style={{
        flex: '6 1 1px',
        background: theme.noVmViewBottomSectionColor || 'rgb(217, 217, 217)',
        display: 'flex',
        flexDirection: 'column',
        marginTop: '14px',
      }}
      >
        <ListContainer>
          <div style={{
            height: '100%',
            width: '92%',
            overflow: 'hidden',
            paddingTop: '2px',
            borderRadius: '12px 12px 0 0',
            background: 'white',
            zIndex: 1,
          }}
          >
            {!error && (
              <ListFiltersContainer>
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '96%',
                  paddingBottom: '10px',
                  fontSize: '0.85em',
                  paddingRight: '16px',
                  paddingLeft: '16px',
                  boxSizing: 'border-box',
                }}
                >
                  <FilterButton
                    selected={(tab === 'ALL')}
                    onClick={() => { handleListChange('ALL'); }}
                    bgColor={theme.buttonColor}
                    inactiveBgColor={theme.filterButtonInactiveColor}
                    inactiveTextColor={theme.filterButtonInactiveTextColor}
                    borderRadius={theme.filterButtonBorderRadius}
                  >
                    {i18n.t('all')}
                  </FilterButton>
                  <Spacer />
                  <FilterButton
                    selected={(tab === 'PURCHASES')}
                    onClick={() => { handleListChange('PURCHASES'); }}
                    bgColor={theme.buttonColor}
                    inactiveBgColor={theme.filterButtonInactiveColor}
                    inactiveTextColor={theme.filterButtonInactiveTextColor}
                    borderRadius={theme.filterButtonBorderRadius}
                  >
                    {i18n.t('movements')}
                  </FilterButton>
                  <Spacer />
                  <FilterButton
                    selected={(tab === 'REVALUES')}
                    onClick={() => { handleListChange('REVALUES'); }}
                    bgColor={theme.buttonColor}
                    inactiveBgColor={theme.filterButtonInactiveColor}
                    inactiveTextColor={theme.filterButtonInactiveTextColor}
                    borderRadius={theme.filterButtonBorderRadius}
                  >
                    {i18n.t('loads')}
                  </FilterButton>
                </div>
              </ListFiltersContainer>
            )}
            <FilteredList>
              {error && !loadingHistory && (
                <>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <h2 style={{ textAlign: 'center', color: 'rgba(99,100,103,255)' }}>{i18n.t('error_ocurred')}</h2>
                    <h2 style={{ textAlign: 'center', color: 'rgba(99,100,103,255)' }}>{i18n.t('try_again_later')}</h2>
                  </Box>
                </>
              )}
              {loadingHistory && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  width="100%"
                  marginTop="25%"
                >
                  <CircularProgress style={{ color: theme.loadingIconColor }} />
                </Box>
              )}
              {!error && dataSource.length === 0 && !loadingHistory && (
                <h2 style={{
                  textAlign: 'center',
                  width: '100%',
                  marginTop: '40%',
                  fontSize: '1.1em',
                  color: 'rgba(99,100,103,255)',
                }}
                >
                  {i18n.t(message)}
                </h2>
              )}
              {!error && dataSource.length > 0 && !loadingHistory && transactionsAccordion}
            </FilteredList>
          </div>
        </ListContainer>
      </div>
    </Root>
  );
};

export default withRouter(NoVMView);
